import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ModalLayout } from "../../components/Modal/ModalLayout";
import { FormField } from "../../components/Form/FormField";
import { useAreas } from "../../hooks/useAreas";
import { useUsers } from "../../hooks/useUsers";
import { useState, useMemo, useRef, useEffect } from "react";
import { debounce } from "lodash";

interface AddAreaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AreaFormValues {
  name: string;
  code: string;
  coordinate: {
    lat: string;
    long: string;
  };
  geometry: string;
  owner: string;
}

interface ApiError {
  success: false;
  err: {
    message: string;
    params: string;
  };
  data: null;
  response?: {
    data: {
      err: {
        message: string;
      };
    };
  };
}

export const AddAreaModal = ({ isOpen, onClose }: AddAreaModalProps) => {
  const { t } = useTranslation();
  const { createArea } = useAreas();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isListOpen, setIsListOpen] = useState(false);
  const { data: users = [], isLoading: isLoadingUsers } = useUsers(searchTerm);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Filter out blocked users
  const activeUsers = useMemo(() => 
    users.filter((user) => !user.isBlocked),
    [users]
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsListOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounce the search to avoid too many API calls
  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
    setIsListOpen(true);
  };

  const handleSelectUser = (userId: string, fullName: string) => {
    formik.setFieldValue('owner', userId);
    setSearchText(fullName);
    setSearchTerm(fullName);
    setIsListOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        setIsListOpen(false);
      }
    }, 200);
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t("validation.required")),
    code: Yup.string().required(t("validation.required")),
    coordinate: Yup.object({
      lat: Yup.number()
        .required(t("validation.required"))
        .typeError(t("validation.number")),
      long: Yup.number()
        .required(t("validation.required"))
        .typeError(t("validation.number")),
    }),
    geometry: Yup.string(),
    owner: Yup.string().required(t("validation.required")),
  });

  const formik = useFormik<AreaFormValues>({
    initialValues: {
      name: "",
      code: "",
      coordinate: {
        lat: "",
        long: "",
      },
      geometry: "",
      owner: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setErrorMessage("");
      const newArea = {
        name: values.name,
        code: values.code,
        coordinate: {
          lat: Number(values.coordinate.lat),
          long: Number(values.coordinate.long),
        },
        geometry: values.geometry,
        owner: values.owner,
      };

      try {
        await createArea.mutateAsync(newArea);
        formik.resetForm();
        onClose();
      } catch (error: unknown) {
        const apiError = error as ApiError;
        setErrorMessage(apiError.response?.data?.err?.message || t("areas.error.generic"));
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    setErrorMessage("");
    formik.resetForm();
    onClose();
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      title={t("areas.modal.addArea")}
    >
      <form onSubmit={formik.handleSubmit}>
        {errorMessage && (
          <div className="alert alert-error mb-4">
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="space-y-4">
          <FormField
            label={t("areas.modal.code")}
            name="code"
            placeholder={t("areas.modal.codePlaceholder")}
            formik={formik as unknown as ReturnType<typeof useFormik>}
          />

          <FormField
            label={t("areas.modal.name")}
            name="name"
            placeholder={t("areas.modal.namePlaceholder")}
            formik={formik as unknown as ReturnType<typeof useFormik>}
          />

          <div>
            <label className="block text-sm font-medium mb-2">
              {t("areas.modal.owner")}
              <span className="text-error">*</span>
            </label>
            <div className="relative" ref={wrapperRef}>
              <input
                type="text"
                className={`input input-bordered w-full pr-10 ${
                  formik.touched.owner && formik.errors.owner ? 'input-error' : ''
                }`}
                placeholder={t("areas.modal.ownerPlaceholder")}
                onChange={handleSearchChange}
                onFocus={() => setIsListOpen(true)}
                onBlur={handleBlur}
                value={searchText}
                disabled={isLoadingUsers}
              />
              {isLoadingUsers && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <span className="loading loading-spinner loading-sm"></span>
                </div>
              )}
              {isListOpen && (
                <div className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md bg-white shadow-lg border border-gray-200">
                  {activeUsers.length > 0 ? (
                    activeUsers.map((user) => (
                      <div
                        key={user._id}
                        className={`cursor-pointer px-4 py-2 hover:bg-gray-100 ${
                          formik.values.owner === user._id ? 'bg-primary/10' : ''
                        }`}
                        onClick={() => handleSelectUser(user._id, user.fullName)}
                      >
                        <div className="font-medium">{user.fullName}</div>
                        <div className="text-sm text-gray-500">{user.phone}</div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500">
                      {t("areas.modal.noUsersFound")}
                    </div>
                  )}
                </div>
              )}
            </div>
            {formik.touched.owner && formik.errors.owner && (
              <div className="text-error text-sm mt-1">{formik.errors.owner}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label={t("areas.modal.latitude")}
              name="coordinate.lat"
              type="number"
              placeholder={t("areas.modal.latitudePlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />

            <FormField
              label={t("areas.modal.longitude")}
              name="coordinate.long"
              type="number"
              placeholder={t("areas.modal.longitudePlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />
          </div>

          <FormField
            label={t("areas.modal.geometry")}
            name="geometry"
            type="textarea"
            placeholder={t("areas.modal.geometryPlaceholder")}
            formik={formik as unknown as ReturnType<typeof useFormik>}
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn btn-ghost" onClick={handleClose}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={formik.isSubmitting}
          >
            {t("areas.modal.addArea")}
          </button>
        </div>
      </form>
    </ModalLayout>
  );
}; 